import { create } from 'zustand'

import { useChartStore } from 'features/charts/store'
import type { DashboardControlsConfig } from 'features/dashboard/types'
import {
  initialFeedbackControlsState,
  useFeedbackControlsStore,
} from 'features/feedback/store'
import {
  initialFilterUIState,
  useFilterStore,
  useFilterUIStore,
} from 'features/filters/store'
import {
  initialKeywordsControlsState,
  useKeywordsControlsStore,
} from 'features/keywords/store'

// Inline helpers (previously in features/project/store/utils)
const removeAtIndex = <T>(arr: T[], idx: number): T[] => [
  ...arr.slice(0, idx),
  ...arr.slice(idx + 1),
]

const mergeObjects = <T extends Record<string, unknown>>(
  source: T,
  target: T,
): T => {
  for (const key in source) {
    if (!target[key]) {
      target[key] = source[key]
    }
  }
  return target
}

// ---------------------------------------------------------------------------
// State type
// ---------------------------------------------------------------------------

export type PanelState = {
  isComparative: boolean
  comparativePanelsNumber: number

  setComparativePanelsNumber: (n: number) => void
  setIsComparative: (v: boolean) => void

  addPanel: () => void
  removePanel: (index: number) => void

  resetPanels: () => void
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

export const initialPanelState = {
  isComparative: false,
  comparativePanelsNumber: 1,
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const usePanelStore = create<PanelState>()((set) => ({
  ...initialPanelState,

  setComparativePanelsNumber: (n) => set({ comparativePanelsNumber: n }),
  setIsComparative: (v) => set({ isComparative: v }),

  addPanel: () => {
    // 1. Read current state from all sub-stores
    const filterState = useFilterStore.getState()
    const chartState = useChartStore.getState()
    const filterUIState = useFilterUIStore.getState()
    const feedbackState = useFeedbackControlsStore.getState()

    // 2. Compute new arrays by cloning first-panel values.
    // `newFilters` clones useFilterStore.filters[0] — for Feedback and
    // Keywords this is stale the moment it lands: filter *content* for those
    // pages lives in useFeedbackFiltersStore/useKeywordsFiltersStore (v2
    // ChartPanel[]), which each reconcile their own panel count off
    // comparativePanelsNumber independently (see either store's
    // `setPanelCount`). This store stays the panel-*count* authority for
    // every page; the v1 clone below is harmless-but-redundant for those two.
    const newFilters = [
      ...filterState.filters,
      JSON.parse(JSON.stringify(filterState.filters[0])),
    ]
    const newFilterList = [...filterUIState.filterList, []]
    const newRelativeDateFilterIdx = [
      ...filterUIState.relativeDateFilterIdx,
      filterUIState.relativeDateFilterIdx[0]!,
    ]
    const newDashboardControls = [
      ...chartState.dashboardControls,
      JSON.parse(JSON.stringify(chartState.dashboardControls[0])),
    ]
    const newKeywordsControls = [
      ...useKeywordsControlsStore.getState().keywordsControls,
      initialKeywordsControlsState.keywordsControls[0]!,
    ]
    const newFeedbackControls = [
      ...feedbackState.feedbackControls,
      initialFeedbackControlsState.feedbackControls[0]!,
    ]
    const newOpenedOptions = [
      ...filterUIState.openedOptions,
      initialFilterUIState.openedOptions[0]!,
    ]
    const newSelectedOptionField = [
      ...filterUIState.selectedOptionField,
      initialFilterUIState.selectedOptionField[0]!,
    ]
    const newCurrentChartList = [
      ...chartState.currentChartList,
      chartState.currentChartList[0]!,
    ]

    // 3. Update panel state
    set((state) => ({
      comparativePanelsNumber: state.comparativePanelsNumber + 1,
      isComparative: true,
    }))

    // Disable legend on first panel when first entering comparative mode
    const panelState = usePanelStore.getState()
    if (panelState.comparativePanelsNumber === 2) {
      newDashboardControls[0] = {
        ...newDashboardControls[0],
        legend: false,
      }
    }

    // 4. Sync sub-stores
    filterState.setFilters(newFilters)
    useFilterUIStore.getState().setFilterList(newFilterList)
    useFilterUIStore
      .getState()
      .setRelativeDateFilterIdx(newRelativeDateFilterIdx)
    useFilterUIStore.getState().setOpenedOptions(newOpenedOptions)
    useFilterUIStore.getState().setSelectedOptionField(newSelectedOptionField)
    useChartStore.getState().setDashboardControls(newDashboardControls)
    useChartStore.getState().setCurrentChartList(newCurrentChartList)
    useKeywordsControlsStore.getState().setKeywordsControls(newKeywordsControls)
    useFeedbackControlsStore.getState().setFeedbackControls(newFeedbackControls)
  },

  removePanel: (index: number) => {
    // 1. Read current state from all sub-stores
    const filterState = useFilterStore.getState()
    const chartState = useChartStore.getState()
    const filterUIState = useFilterUIStore.getState()

    // 2. Compute new arrays by removing at the given index
    const newFilters = removeAtIndex(filterState.filters, index)
    const newFilterList = removeAtIndex(filterUIState.filterList, index)
    const newRelativeDateFilterIdx = removeAtIndex(
      filterUIState.relativeDateFilterIdx,
      index,
    )

    let newDashboardControls = chartState.dashboardControls
    if (index === 0) {
      const newZeroIndex = mergeObjects(
        { ...newDashboardControls[0] },
        { ...newDashboardControls[1] },
      )
      newDashboardControls = removeAtIndex(newDashboardControls, index)
      newDashboardControls[0] = newZeroIndex as DashboardControlsConfig
    } else {
      newDashboardControls = removeAtIndex(newDashboardControls, index)
    }

    const newKeywordsControls = removeAtIndex(
      useKeywordsControlsStore.getState().keywordsControls,
      index,
    )
    const newFeedbackControls = removeAtIndex(
      useFeedbackControlsStore.getState().feedbackControls,
      index,
    )
    const newOpenedOptions = removeAtIndex(filterUIState.openedOptions, index)
    const newSelectedOptionField = removeAtIndex(
      filterUIState.selectedOptionField,
      index,
    )
    const newChartData = removeAtIndex(chartState.chartData, index)
    const newCurrentChartList = removeAtIndex(
      chartState.currentChartList,
      index,
    )

    // 3. Update panel state
    let newPanelsNumber = 0
    let newIsComparative = false
    set((state) => {
      newPanelsNumber = state.comparativePanelsNumber - 1
      newIsComparative = newPanelsNumber > 1
      return {
        comparativePanelsNumber: newPanelsNumber,
        isComparative: newIsComparative,
      }
    })

    // Re-enable legend when exiting comparative mode
    if (!newIsComparative) {
      newDashboardControls[0] = {
        ...newDashboardControls[0],
        legend: true,
      } as DashboardControlsConfig
    }

    // 4. Sync sub-stores
    filterState.setFilters(newFilters)
    useFilterUIStore.getState().setFilterList(newFilterList)
    useFilterUIStore
      .getState()
      .setRelativeDateFilterIdx(newRelativeDateFilterIdx)
    useFilterUIStore.getState().setOpenedOptions(newOpenedOptions)
    useFilterUIStore.getState().setSelectedOptionField(newSelectedOptionField)
    useChartStore.getState().setDashboardControls(newDashboardControls)
    useChartStore.getState().setChartData(newChartData)
    useChartStore.getState().setCurrentChartList(newCurrentChartList)
    useKeywordsControlsStore.getState().setKeywordsControls(newKeywordsControls)
    useFeedbackControlsStore.getState().setFeedbackControls(newFeedbackControls)
  },

  resetPanels: () => set(initialPanelState),
}))
